import React from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import { AppointmentTypes } from '../../../types/appointment.types';
import { UPCOMING_COLUMNS } from '../../../builders/appointments-builders';
import { LocationCell } from './location-cell';
import { PingIndicator } from './ping-indicator';
import { deriveLastPingTime } from '../users-list/last-ping-source';

/**
 * Upcoming section for the Active & Future tab.
 *
 * Shows future appointments (not yet in progress) with:
 *   - Duration computed from start/end
 *   - Last device ping indicator
 */

interface Props {
    items: AppointmentTypes.Model[];
    onRowClick: (appointment: AppointmentTypes.Model) => void;
}

export const UpcomingSection: React.FC<Props> = ({ items, onRowClick }) => {
    const theme = useTheme();

    if (!items || items.length === 0) return null;

    const formatDuration = (start: Date | undefined, end: Date | undefined): string => {
        if (!start || !end) return '--';
        const diffMins = dayjs(end).diff(dayjs(start), 'minute');
        if (diffMins < 60) return `${diffMins}m`;
        const hrs = Math.floor(diffMins / 60);
        const mins = diffMins % 60;
        return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
    };

    return (
        <Box>
            {/* Section header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                    Upcoming
                </Typography>
                <Box
                    sx={{
                        backgroundColor: theme.palette.grey[200],
                        borderRadius: 1,
                        px: 1,
                        py: 0.25,
                    }}
                >
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {items.length}
                    </Typography>
                </Box>
            </Box>

            {/* Table */}
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {UPCOMING_COLUMNS.map((col) => (
                                <TableCell key={col.field}>
                                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                                        {col.headerName}
                                    </Typography>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((appt) => (
                            <TableRow
                                key={appt.id}
                                sx={{
                                    ':hover': {
                                        backgroundColor: theme.palette.action.hover,
                                        cursor: 'pointer',
                                    },
                                }}
                                onClick={() => onRowClick(appt)}
                            >
                                {/* Scheduled Time */}
                                <TableCell>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {appt.startDateTime
                                            ? dayjs(appt.startDateTime).format('MM/DD/YYYY h:mm A')
                                            : '--'}
                                    </Typography>
                                </TableCell>

                                {/* Title */}
                                <TableCell>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {appt.title || '--'}
                                    </Typography>
                                </TableCell>

                                {/* User Name */}
                                <TableCell>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {appt.user?.firstName && appt.user?.lastName
                                            ? `${appt.user.firstName} ${appt.user.lastName}`
                                            : '[Deleted User]'}
                                    </Typography>
                                </TableCell>

                                {/* Appt Location */}
                                <TableCell>
                                    <LocationCell location={appt.location} />
                                </TableCell>

                                {/* Duration */}
                                <TableCell>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {formatDuration(appt.startDateTime, appt.endDateTime)}
                                    </Typography>
                                </TableCell>

                                {/* Last Ping */}
                                <TableCell>
                                    <PingIndicator
                                        lastPing={deriveLastPingTime(appt.user, [
                                            appt.lastDevicePing,
                                        ])}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};
